import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { loadAccess } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;

    const poll = async () => {
      if (attempts.current >= 8) { if (active) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("paid");
          await loadAccess();
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (active) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, loadAccess]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-5 text-center">
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <h1 className="mt-6 font-head text-3xl font-bold">Confirming Payment…</h1>
          <p className="mt-2 text-muted-foreground">Hang tight while we verify your transaction.</p>
        </>
      )}
      {state === "paid" && (
        <div data-testid="checkout-success">
          <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
          <h1 className="mt-6 font-head text-4xl font-black">You're In.</h1>
          <p className="mt-3 text-muted-foreground">
            {info?.type === "annual_enrollment"
              ? "Your annual enrollment is active."
              : info?.type === "cover_school"
              ? "Your Hornsby Church School enrollment is active. Your documents are ready in your portal."
              : "Your monthly membership is active."} {info?.type !== "cover_school" && "Access has been unlocked."}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            {info?.type === "cover_school" ? (
              <>
                <Link to="/dashboard"><Button className="rounded-none label-caps bg-primary hover:bg-primary/80">View My Documents</Button></Link>
                <Link to="/cover-school/document/welcome"><Button variant="outline" className="rounded-none label-caps">Welcome Letter</Button></Link>
              </>
            ) : (
              <>
                <Link to="/dashboard"><Button className="rounded-none label-caps bg-primary hover:bg-primary/80">Go to Members Area</Button></Link>
                <Link to="/curriculum/browse"><Button variant="outline" className="rounded-none label-caps">Browse Courses</Button></Link>
              </>
            )}
          </div>
        </div>
      )}
      {(state === "failed" || state === "timeout") && (
        <div data-testid="checkout-failed">
          <XCircle className="mx-auto h-14 w-14 text-destructive" />
          <h1 className="mt-6 font-head text-3xl font-bold">{state === "timeout" ? "Still Processing" : "Payment Not Completed"}</h1>
          <p className="mt-3 text-muted-foreground">{state === "timeout" ? "This is taking longer than expected. Check your Members Area shortly." : "We couldn't confirm your payment. Please try again."}</p>
          <Link to="/pricing"><Button className="mt-8 rounded-none label-caps bg-primary hover:bg-primary/80">Back to Enrollment</Button></Link>
        </div>
      )}
    </div>
  );
}
